import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X, LogIn } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import './GuestSignInBanner.css';

/**
 * GuestSignInBanner
 *
 * A single, non-blocking sticky toast that nudges anonymous browsers to
 * sign in. Rendered ONCE at the app root so we never end up with multiple
 * banners.
 *
 * Visibility rules (all must hold):
 *   - User is not authenticated (auth has finished loading)
 *   - Not on /auth, /welcome, /onboarding (where a sign-in CTA is already
 *     the primary action — banner would be redundant noise there)
 *   - User has not dismissed it this session
 *   - At least `SHOW_AFTER_MS` has elapsed since mount OR the user has
 *     scrolled past `SHOW_AFTER_SCROLL_PX`
 *
 * Position: bottom-sheet style, sitting ABOVE the bottom navigation so it
 * never overlaps the floating post button or the nav itself. Maximum
 * z-index slightly below modals so any auth/legal modal still wins.
 *
 * Dismissal: closing flips a `sessionStorage` flag → hidden until the next
 * fresh tab/session. Signing in flips an in-memory flag → never shown
 * again for that account.
 */
const SESSION_DISMISS_KEY = 'wassla.guestSignInBanner.dismissed';
const SHOW_AFTER_MS = 3500;       // give the page some time to settle
const SHOW_AFTER_SCROLL_PX = 240; // …or this much scroll, whichever comes first

const SUPPRESSED_PATHS = ['/auth', '/welcome', '/onboarding', '/auth/callback'];

function GuestSignInBanner() {
  const { t, i18n } = useTranslation();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  // iter324 — Track whether the Home page has flagged Near Me as the
  // active discovery surface. When it has, this banner's sticky bottom
  // position would cover the Near Me action buttons (Use my location /
  // Choose your city / Sign in) and the user reported the banner
  // confused them. Suppressing while Near Me is active is the safest
  // fix because the Near Me card itself already exposes a Sign-in CTA
  // for guests, so the helper toast is redundant on that surface.
  const [nearMeActive, setNearMeActive] = useState(
    () => typeof document !== 'undefined' && document.body.getAttribute('data-nearme-active') === '1'
  );
  useEffect(() => {
    if (typeof MutationObserver === 'undefined') return;
    const refresh = () => setNearMeActive(document.body.getAttribute('data-nearme-active') === '1');
    refresh();
    const observer = new MutationObserver(refresh);
    observer.observe(document.body, { attributes: true, attributeFilter: ['data-nearme-active'] });
    return () => observer.disconnect();
  }, []);
  const isRTL = i18n.language === 'ar';

  // Reset visibility whenever the auth state flips.
  useEffect(() => {
    if (user) {
      setVisible(false);
      return;
    }
    if (loading) return;

    // Already dismissed this session → never show again until tab close.
    try {
      if (sessionStorage.getItem(SESSION_DISMISS_KEY) === '1') return;
    } catch {
      // sessionStorage may be unavailable in some private modes — fall
      // through and let the banner show once. We accept the risk.
    }

    // Don't surface on auth/welcome surfaces.
    if (SUPPRESSED_PATHS.some((p) => location.pathname.startsWith(p))) {
      return;
    }

    let scrolled = false;
    const onScroll = () => {
      if (scrolled) return;
      if (window.scrollY > SHOW_AFTER_SCROLL_PX) {
        scrolled = true;
        setVisible(true);
      }
    };
    const t1 = setTimeout(() => setVisible(true), SHOW_AFTER_MS);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      clearTimeout(t1);
      window.removeEventListener('scroll', onScroll);
    };
  }, [user, loading, location.pathname]);

  if (!visible) return null;
  if (user) return null;
  if (SUPPRESSED_PATHS.some((p) => location.pathname.startsWith(p))) {
    return null;
  }
  // iter324 — Hide while Near Me is the active surface so the toast
  // never covers the Near Me action buttons. The Near Me card itself
  // exposes a "Sign in" CTA for guests, so this banner is redundant
  // there.
  if (nearMeActive) return null;

  const handleSignIn = () => {
    try {
      sessionStorage.setItem(SESSION_DISMISS_KEY, '1');
    } catch {
      // ignore
    }
    setVisible(false);
    navigate('/auth?mode=login');
  };

  const handleDismiss = (e) => {
    e?.stopPropagation();
    try {
      sessionStorage.setItem(SESSION_DISMISS_KEY, '1');
    } catch {
      // ignore
    }
    setVisible(false);
  };

  return (
    <div
      className="guest-signin-banner"
      role="region"
      aria-live="polite"
      dir={isRTL ? 'rtl' : 'ltr'}
      data-testid="guest-signin-banner"
    >
      <button
        type="button"
        className="guest-signin-banner__body"
        onClick={handleSignIn}
        data-testid="guest-signin-banner-cta"
      >
        <span className="guest-signin-banner__icon" aria-hidden="true">
          <LogIn size={18} />
        </span>
        <span className="guest-signin-banner__text">
          {t('guestSignInPrompt')}
        </span>
      </button>
      <button
        type="button"
        className="guest-signin-banner__close"
        onClick={handleDismiss}
        aria-label={t('close')}
        data-testid="guest-signin-banner-close"
      >
        <X size={16} />
      </button>
    </div>
  );
}

export default GuestSignInBanner;
